import { AttachmentTypes } from 'types/api';
import { Attachment as AttachmentType } from 'types/clients';

import {
  AttachmentsOwnerEntities,
  JobseekerAttachmentTypes
} from 'constants/enums';

interface UseIsShowAttachmentsProps {
  isSidebarMode: boolean;
  attachments: AttachmentType<AttachmentTypes>[] | null;
  attachmentsOwnerEntity: AttachmentsOwnerEntities;
}

const useIsShowAttachments = ({
  isSidebarMode,
  attachments,
  attachmentsOwnerEntity
}: UseIsShowAttachmentsProps): {
  isShowCV: boolean;
  isShowPassport: boolean;
  isShowIdCard: boolean;
  isShowPersonalDocumentsSection: boolean;
  isShowVisa: boolean;
  isShowWorkPermit: boolean;
  isShowOfferLetter: boolean;
  isShowImmigrationDocumentsSection: boolean;
  isShowDriverLicense: boolean;
  isShowOtherJobseekersDocuments: boolean;
} => {
  const VISIBLE_ATTACHMENT_TYPES_MAP = {
    [AttachmentsOwnerEntities.Jobseeker]: [
      JobseekerAttachmentTypes.CV,
      JobseekerAttachmentTypes.Passport,
      JobseekerAttachmentTypes.IdCard,
      JobseekerAttachmentTypes.Visa,
      JobseekerAttachmentTypes.WorkPermit,
      JobseekerAttachmentTypes.OfferLetter,
      JobseekerAttachmentTypes.DriverLicense,
      JobseekerAttachmentTypes.OtherJobseekerDocuments
    ],

    [AttachmentsOwnerEntities.Application]: [
      JobseekerAttachmentTypes.CV,
      JobseekerAttachmentTypes.OfferLetter
    ]
  } as Record<AttachmentsOwnerEntities, AttachmentTypes[]>;

  const isShowAttachmentsOfType = (
    attachmentType: AttachmentTypes
  ): boolean => {
    const isShowAttachment =
      VISIBLE_ATTACHMENT_TYPES_MAP[attachmentsOwnerEntity] &&
      VISIBLE_ATTACHMENT_TYPES_MAP[attachmentsOwnerEntity].includes(
        attachmentType
      );

    if (!attachments || !isShowAttachment) {
      return false;
    }

    const isIncludesAttachmentOfType = Boolean(
      attachments?.some((attachment) => attachment.type === attachmentType)
    );

    return isIncludesAttachmentOfType || !isSidebarMode;
  };

  const isShowCV = isShowAttachmentsOfType(JobseekerAttachmentTypes.CV);

  const isShowPassport = isShowAttachmentsOfType(
    JobseekerAttachmentTypes.Passport
  );

  const isShowIdCard = isShowAttachmentsOfType(JobseekerAttachmentTypes.IdCard);

  const isShowDriverLicense = isShowAttachmentsOfType(
    JobseekerAttachmentTypes.DriverLicense
  );

  const isShowOtherJobseekersDocuments = isShowAttachmentsOfType(
    JobseekerAttachmentTypes.OtherJobseekerDocuments
  );

  const isShowPersonalDocumentsSection =
    isShowPassport ||
    isShowIdCard ||
    isShowDriverLicense ||
    isShowOtherJobseekersDocuments;

  const isShowVisa = isShowAttachmentsOfType(JobseekerAttachmentTypes.Visa);

  const isShowWorkPermit = isShowAttachmentsOfType(
    JobseekerAttachmentTypes.WorkPermit
  );

  const isShowImmigrationDocumentsSection = isShowVisa || isShowWorkPermit;

  const isShowOfferLetter = isShowAttachmentsOfType(
    JobseekerAttachmentTypes.OfferLetter
  );

  return {
    isShowCV,
    isShowPassport,
    isShowIdCard,
    isShowPersonalDocumentsSection,
    isShowVisa,
    isShowWorkPermit,
    isShowOfferLetter,
    isShowImmigrationDocumentsSection,
    isShowDriverLicense,
    isShowOtherJobseekersDocuments
  };
};

export { useIsShowAttachments };
